import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { useDialogController } from '@machobear/design-system'
import { RootState, select } from '@/store'
import useRole from '@/hooks/use-role'
import { ApplicationView } from '@/pages/Application/ApplicationView'
import { ApplicationViewV2 } from '@/pages/Application/ApplicationViewV2'
import useCurrentSession from '@/hooks/use-current-session'
import VerifyIdDialog from './verify-id-dialog'

interface MatchParams {
  applicationId: string
  listingId?: string
  timelineId: string
}

interface ApplicationDispatch {
  application: {
    loadNoLoading: (id: string) => void
    load: (id: string) => void
    getApplicationTrustScore: (id: string) => void
    viewApplication: (id: string) => void
  }
}

interface Props extends RouteComponentProps<MatchParams> {
  history: RouteComponentProps['history']
  location: RouteComponentProps['location']
  sidebarExpanded: boolean
}

interface LocationState {
  prev?: string
}

const ApplicationViewSelect: React.FC<Props> = ({
  match: {
    params: { applicationId, listingId, timelineId }
  },
  history,
  location,
  sidebarExpanded
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [applicationViewed, setApplicationViewed] = useState(false)
  const { visible, showModal, closeModal } = useDialogController()
  const role = useRole()
  const isLandlord = role === 'landlord'
  const dispatch = useDispatch<ApplicationDispatch>()
  const application = useSelector((state: RootState) =>
    (select as any).application.applicationDetail(state, {
      applicationId
    })
  )
  const idState = useCurrentSession()?.identity_verified_state
  const isUserIdVerified = idState === 'verified'
  const isUserIdPending = idState === 'pending'
  const applicationAvailable = !!application.id && !!application.listing_info
  const locationState = location.state as LocationState
  const prevRoute = locationState?.prev

  useEffect(() => {
    if (!isLandlord) return
    if (isUserIdPending) {
      history.replace(`/application/${applicationId}`, location.state)
      showModal()
    } else if (!isUserIdVerified) {
      history.replace('/profile?openVerifyUserModal=true', { state: { prev: location.pathname } })
    }
  }, [isLandlord, isUserIdPending, isUserIdVerified, applicationId, history])

  useEffect(() => {
    if (isDataLoaded) return
    const loadApplication = async () => {
      const action = isLandlord ? 'loadNoLoading' : 'load'
      await dispatch.application?.[action](applicationId)
      setIsDataLoaded(true)
    }
    loadApplication()
  }, [isLandlord, applicationId, dispatch.application, isDataLoaded])

  useEffect(() => {
    if (applicationViewed || !isLandlord || !applicationAvailable || !isDataLoaded) return
    const viewApplication = async () => {
      await dispatch.application.viewApplication(applicationId)
      setApplicationViewed(true)
    }
    viewApplication()
  }, [
    applicationId,
    dispatch.application,
    applicationViewed,
    isLandlord,
    applicationAvailable,
    isDataLoaded
  ])

  const handleCloseModal = () => {
    !!prevRoute ? history.goBack() : history.push('/app/listings')
    closeModal()
  }

  return (
    <>
      {role === 'tenant' ? (
        <ApplicationView
          match={{ params: { applicationId, listingId, timelineId } }}
          history={history}
        />
      ) : (
        <ApplicationViewV2
          match={{ params: { applicationId, timelineId } }}
          history={history}
          location={location}
          sidebarExpanded={sidebarExpanded}
        />
      )}
      <VerifyIdDialog open={visible} onClose={handleCloseModal} />
    </>
  )
}

export default ApplicationViewSelect
